import { useParams, Link } from 'react-router-dom';
import { allArticles, categories } from '@/mocks/sports';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SchemaWebPage, SchemaBreadcrumbList, SchemaArticle } from '@/components/feature/SchemaOrg';
import { siteInfo } from '@/mocks/sports';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-20 pb-20 text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground-900">文章未找到</h1>
        <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SchemaWebPage
        url={`/article/${article.slug}`}
        name={article.title}
        description={article.excerpt}
      />
      <SchemaBreadcrumbList
        items={[
          { name: '首页', url: '/' },
          { name: category?.name || article.category, url: `/category/${article.category}` },
          { name: article.title, url: `/article/${article.slug}` },
        ]}
      />
      <SchemaArticle
        article={{
          title: article.title,
          excerpt: article.excerpt,
          date: article.date,
          slug: article.slug,
          image: article.image,
          category: article.category,
          categoryName: category?.name || article.category,
        }}
      />

      <div className="pt-24 pb-10 bg-background-100">
        <div className="w-full px-4 md:px-6 mb-6">
          <Breadcrumb
            items={[
              { name: category?.name || article.category, path: `/category/${article.category}` },
              { name: article.title },
            ]}
          />
        </div>
      </div>

      <article className="w-full pb-14 md:pb-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4"
              >
                {category?.name || article.category}
              </Link>
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读时间 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>
            </div>

            <div className="relative h-[300px] md:h-[450px] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800">
              <p className="text-base md:text-lg leading-relaxed mb-4">
                {article.excerpt}
              </p>
              <p className="text-sm md:text-base leading-relaxed mb-4">
                在九游体育平台，我们为您提供最全面、最及时的{category?.name || '体育'}赛事报道。每一场比赛背后都有着令人振奋的故事，
                我们的专业分析团队深入赛场第一线，为您带来独家的赛事解读和深度分析。
              </p>
              <p className="text-sm md:text-base leading-relaxed mb-4">
                本场比赛吸引了全球数亿观众的关注，各大媒体纷纷聚焦此次赛事。从赛前预测到赛后复盘，九游体育为您提供全方位的赛事服务。
                我们的数据团队通过人工智能和大数据分析技术，对比赛的每一个细节进行深度挖掘，帮助您更好地理解比赛走势。
              </p>
              <p className="text-sm md:text-base leading-relaxed mb-4">
                赛事数据分析显示，本次比赛的关键因素包括球队战术部署、核心球员状态以及临场应变能力。
                九游体育数据分析团队将持续跟踪赛事动态，为您带来最新、最权威的赛事解读。
              </p>
              <p className="text-sm md:text-base leading-relaxed mb-4">
                加入九游体育，与数千万体育爱好者一起，享受极致的体育娱乐体验。无论您是资深球迷还是刚刚接触体育的新朋友，
                我们的平台都能满足您对赛事资讯的各种需求。覆盖全球50+体育联盟，实时更新赛事数据，让您不会错过任何精彩瞬间。
              </p>
            </div>

            <div className="mt-8 p-4 md:p-6 bg-background-100 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-user-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">九游体育编辑部</p>
                  <p className="text-xs text-foreground-500">专业体育赛事分析团队</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="w-full py-14 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((related) => (
                  <article key={related.slug} className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer">
                    <Link to={`/article/${related.slug}`} className="block h-full">
                      <div className="relative h-40 md:h-48 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          title={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-1">
                          {related.title}
                        </h3>
                        <span className="text-xs text-foreground-400">{related.date}</span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}